/**
 * Customer Pericias List PDF Creator - Converted to use @react-pdf/renderer
 *
 * This component has been converted from the old jsPDF implementation
 * to use the new @react-pdf/renderer library, following the same pattern as
 * the other PDF components.
 *
 * Changes made:
 * - Replaced jsPDF with @react-pdf/renderer
 * - Created CostumerPericiasListPDFDocument component using Document/Page/View/Text
 * - Used built-in fonts instead of external font registration
 * - Replaced manual PDF generation with declarative component structure
 * - Added proper page content wrapper structure
 */

import React from "react";
import { Document, Page, Text, View, StyleSheet } from "@react-pdf/renderer";
import { Pericia, getPericiaCustomPriceCurrencyFormat } from "../../../../entities/pericia";

interface CostumerPericiasListPDFProps {
  pericias: Pericia[];
  costumerName: string;
  start: Date;
  end: Date;
  totalPrice: string;
}

// PDF styles using react-pdf
const styles = StyleSheet.create({
  page: {
    fontSize: 12,
    padding: 15,
    backgroundColor: "#ffffff",
    color: "#000",
    lineHeight: 1.4
  },
  pageContent: {
    padding: 15,
    minHeight: "95%"
  },
  header: {
    marginBottom: 20
  },
  headerInfo: {
    alignItems: "flex-start",
    textAlign: "left"
  },
  title: {
    fontSize: 12,
    fontWeight: "bold",
    marginBottom: 2
  },
  subtitle: {
    fontSize: 11,
    marginBottom: 2
  },
  tableHeader: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#000",
    paddingBottom: 5,
    marginBottom: 10,
    marginTop: 20
  },
  tableHeaderCell: {
    flex: 1,
    fontSize: 10,
    fontWeight: "bold",
    textAlign: "left"
  },
  tableRow: {
    flexDirection: "row",
    paddingVertical: 3,
    borderBottomWidth: 0.5,
    borderBottomColor: "#ccc"
  },
  tableCell: {
    flex: 1,
    fontSize: 9,
    textAlign: "left"
  },
  totalRow: {
    flexDirection: "row",
    justifyContent: "flex-end",
    marginTop: 15,
    paddingTop: 10,
    borderTopWidth: 1,
    borderTopColor: "#000"
  },
  totalText: {
    fontSize: 11,
    fontWeight: "bold"
  }
});

// Helper function to split pericias into pages (28 items per page to match original)
const splitPericiasIntoPages = (pericias: Pericia[]): Pericia[][] => {
  const pages: Pericia[][] = [];
  const itemsPerPage = 28;

  for (let i = 0; i < pericias.length; i += itemsPerPage) {
    pages.push(pericias.slice(i, i + itemsPerPage));
  }

  return pages;
};

// Main PDF Document Component
const CostumerPericiasListPDFDocument: React.FC<CostumerPericiasListPDFProps> = ({ pericias, costumerName, start, end, totalPrice }) => {
  const pages = splitPericiasIntoPages(pericias);
  const formattedStartDate = start.toLocaleDateString("pt-BR");
  const formattedEndDate = end.toLocaleDateString("pt-BR");

  return (
    <Document>
      {pages.map((pageItems, pageIndex) => (
        <Page key={pageIndex} size="A4" style={styles.page}>
          <View style={styles.pageContent}>
            {/* Header - only on first page */}
            {pageIndex === 0 && (
              <View style={styles.header}>
                <View style={styles.headerInfo}>
                  <Text style={styles.title}>Cliente: {costumerName}</Text>
                  <Text style={styles.subtitle}>
                    Período: {formattedStartDate} - {formattedEndDate}
                  </Text>
                </View>
              </View>
            )}

            {/* Table Header */}
            <View style={styles.tableHeader}>
              <Text style={[styles.tableHeaderCell, { flex: 1.5 }]}>PLACA</Text>
              <Text style={[styles.tableHeaderCell, { flex: 2 }]}>MODELO</Text>
              <Text style={[styles.tableHeaderCell, { flex: 1.5 }]}>DATA</Text>
              <Text style={[styles.tableHeaderCell, { flex: 1.5 }]}>PREÇO</Text>
            </View>

            {/* Table Rows */}
            {pageItems.map((pericia) => {
              const { car, date } = pericia;
              const { plate, model } = car;
              const formattedDate = new Date(date).toLocaleDateString("pt-BR");
              const price = getPericiaCustomPriceCurrencyFormat(pericia);

              return (
                <View key={pericia.id} style={styles.tableRow}>
                  <Text style={[styles.tableCell, { flex: 1.5 }]}>{plate}</Text>
                  <Text style={[styles.tableCell, { flex: 2 }]}>{model}</Text>
                  <Text style={[styles.tableCell, { flex: 1.5 }]}>{formattedDate}</Text>
                  <Text style={[styles.tableCell, { flex: 1.5 }]}>{price}</Text>
                </View>
              );
            })}

            {/* Total - only on last page */}
            {pageIndex === pages.length - 1 && (
              <View style={styles.totalRow}>
                <Text style={styles.totalText}>Total: {totalPrice}</Text>
              </View>
            )}
          </View>
        </Page>
      ))}
    </Document>
  );
};

export type { CostumerPericiasListPDFProps };
export { CostumerPericiasListPDFDocument };
